import { Plus, Minus } from 'lucide-react';

const UNIT_OPTIONS_BRAKE = [
  { value: 'mm', label: 'mm' },
  { value: 'pouces', label: 'Pouces' },
  { value: 'pourcent', label: '%' },
];

const UNIT_OPTIONS_TIRE = [
  { value: 'mm', label: 'mm' },
  { value: 'pouces', label: 'Pouces' },
  { value: '32emes', label: '32emes' },
  { value: 'pourcent', label: '%' },
];

const getColor = (value, type, dark) => {
  const empty = dark
    ? { bg: 'bg-slate-800', border: 'border-slate-600', bar: 'bg-slate-600' }
    : { bg: 'bg-slate-100', border: 'border-slate-300', bar: 'bg-slate-300' };
  if (value === null || value === undefined || value === '') return empty;
  const v = parseFloat(value);
  if (isNaN(v)) return empty;
  if (type === 'brake') {
    if (v >= 5) return { bg: dark ? 'bg-green-900/30' : 'bg-green-50', border: 'border-green-400', bar: 'bg-green-500' };
    if (v >= 3) return { bg: dark ? 'bg-amber-900/30' : 'bg-amber-50', border: 'border-amber-400', bar: 'bg-amber-500' };
    return { bg: dark ? 'bg-red-900/30' : 'bg-red-50', border: 'border-red-400', bar: 'bg-red-500' };
  }
  if (v >= 6) return { bg: dark ? 'bg-green-900/30' : 'bg-green-50', border: 'border-green-400', bar: 'bg-green-500' };
  if (v >= 4) return { bg: dark ? 'bg-amber-900/30' : 'bg-amber-50', border: 'border-amber-400', bar: 'bg-amber-500' };
  return { bg: dark ? 'bg-red-900/30' : 'bg-red-50', border: 'border-red-400', bar: 'bg-red-500' };
};

const MeasurementCell = ({ value, onChange, type, disabled, dark }) => {
  const color = getColor(value, type, dark);
  return (
    <div className={`relative rounded-lg border-2 ${color.border} ${color.bg} overflow-hidden transition-all`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${color.bar}`} />
      <input
        type="number"
        step="0.1"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        disabled={disabled}
        className={`w-full bg-transparent text-center font-bold text-sm py-2.5 px-1 focus:outline-none ${
          disabled ? (dark ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 cursor-not-allowed')
                   : (dark ? 'text-white' : 'text-black')
        }`}
        placeholder="-"
      />
    </div>
  );
};

export default function AxleDiagram({
  axles, brakeUnit, tireUnit,
  onUpdateAxle, onToggleDual, onAddAxle, onRemoveAxle,
  onBrakeUnitChange, onTireUnitChange,
  disabled, dark = false, showUnits = true, compact = false,
}) {
  const unitLabel = (u) => ({ mm: 'mm', pouces: 'po', '32emes': '/32', pourcent: '%' }[u] || u);
  const textPrimary = dark ? 'text-white' : 'text-black';
  const textSecondary = dark ? 'text-slate-400' : 'text-slate-500';
  const textMuted = dark ? 'text-slate-500' : 'text-slate-400';
  const bgCard = dark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-200';
  const bgSub = dark ? 'bg-slate-700/50' : 'bg-slate-50';
  const bgCenter = dark ? 'bg-slate-700' : 'bg-black';
  const selectCls = dark
    ? 'w-full rounded-md border border-slate-600 bg-slate-800 py-2 px-3 text-sm text-white focus:outline-none focus:border-amber-500'
    : 'w-full rounded-md border border-slate-300 bg-white py-2 px-3 text-sm text-black focus:outline-none focus:border-amber-500';
  const btnOutline = dark
    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700 px-3 py-2 rounded-lg text-sm font-medium'
    : 'border border-slate-300 text-black hover:bg-slate-50 px-3 py-2 rounded-lg text-sm font-medium';

  return (
    <div className="space-y-3" data-testid="axle-diagram">
      {/* Units + Add/Remove */}
      {showUnits && (
        <div className={`flex flex-wrap items-end gap-3`}>
          <div className="flex-1 min-w-[120px]">
            <label className={`block text-xs ${textSecondary} mb-1`}>Unite freins</label>
            <select value={brakeUnit || 'mm'} onChange={e => onBrakeUnitChange?.(e.target.value)} disabled={disabled}
              className={selectCls} data-testid="brake-unit-select">
              {UNIT_OPTIONS_BRAKE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className={`block text-xs ${textSecondary} mb-1`}>Unite pneus</label>
            <select value={tireUnit || 'mm'} onChange={e => onTireUnitChange?.(e.target.value)} disabled={disabled}
              className={selectCls} data-testid="tire-unit-select">
              {UNIT_OPTIONS_TIRE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {!disabled && (
            <div className="flex items-end gap-2">
              <button type="button" onClick={() => onAddAxle?.(true)} className={btnOutline} data-testid="add-axle-btn">
                <Plus size={14} className="inline mr-1" /> Essieu
              </button>
              <button type="button" onClick={onRemoveAxle} className={btnOutline} disabled={axles.length <= 1}
                style={{ opacity: axles.length <= 1 ? 0.4 : 1 }}>
                <Minus size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Visual Axle Diagram */}
      <div className={`border rounded-xl p-4 shadow-sm overflow-x-auto ${bgCard}`}>
        <div className={compact ? 'min-w-[500px]' : 'min-w-[600px]'}>
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_auto_1fr_1fr_1fr] gap-1 mb-1 items-end">
            <div className="col-span-3 text-center mb-1">
              <span className={`text-xs font-bold ${textPrimary} tracking-wider`}>GAUCHE</span>
            </div>
            <div />
            <div className="col-span-3 text-center mb-1">
              <span className={`text-xs font-bold ${textPrimary} tracking-wider`}>DROIT</span>
            </div>
          </div>

          {/* Sub-headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_80px_1fr_1fr_1fr] gap-1 mb-2">
            <div className={`text-center text-[10px] font-semibold ${textSecondary} ${bgSub} rounded py-1`}>Pneu Ext</div>
            <div className={`text-center text-[10px] font-semibold ${textSecondary} ${bgSub} rounded py-1`}>Pneu Int</div>
            <div className={`text-center text-[10px] font-semibold ${textSecondary} ${bgSub} rounded py-1`}>Freins</div>
            <div className={`text-center text-[10px] font-semibold ${textMuted} py-1`}>AVANT</div>
            <div className={`text-center text-[10px] font-semibold ${textSecondary} ${bgSub} rounded py-1`}>Freins</div>
            <div className={`text-center text-[10px] font-semibold ${textSecondary} ${bgSub} rounded py-1`}>Pneu Int</div>
            <div className={`text-center text-[10px] font-semibold ${textSecondary} ${bgSub} rounded py-1`}>Pneu Ext</div>
          </div>

          {/* Axle rows */}
          {axles.map((axle, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_80px_1fr_1fr_1fr] gap-1 mb-1.5" data-testid={`axle-row-${axle.axle_number}`}>
              {/* LEFT: Pneu Ext */}
              <MeasurementCell value={axle.left_tire_outer} onChange={v => onUpdateAxle(idx, 'left_tire_outer', v)} type="tire" disabled={disabled || !axle.has_dual_tires} dark={dark} />
              {/* LEFT: Pneu Int */}
              <MeasurementCell value={axle.left_tire_inner} onChange={v => onUpdateAxle(idx, 'left_tire_inner', v)} type="tire" disabled={disabled} dark={dark} />
              {/* LEFT: Freins */}
              <MeasurementCell value={axle.left_brake} onChange={v => onUpdateAxle(idx, 'left_brake', v)} type="brake" disabled={disabled} dark={dark} />

              {/* CENTER: Vehicle body / Axle */}
              <div className="relative flex items-center justify-center">
                <div className={`absolute inset-y-0 left-0 right-0 ${bgCenter} rounded`} />
                <div className="absolute left-[-4px] right-[-4px] h-[3px] bg-slate-600 top-1/2 -translate-y-1/2" />
                <div className="relative z-10 text-white text-xs font-bold whitespace-nowrap">
                  E{axle.axle_number}
                </div>
                {!disabled && (
                  <button type="button" onClick={() => onToggleDual(idx)}
                    className={`absolute -bottom-0.5 right-0.5 z-20 text-[8px] rounded px-1 border ${
                      dark ? 'bg-slate-700 text-slate-300 border-slate-500 hover:bg-amber-500/20 hover:text-amber-400'
                           : 'bg-white text-slate-500 border-slate-300 hover:bg-amber-50 hover:text-amber-600'
                    }`}
                    title={axle.has_dual_tires ? 'Pneu simple' : 'Pneu double'}
                    data-testid={`toggle-dual-${idx}`}
                  >
                    {axle.has_dual_tires ? '2x' : '1x'}
                  </button>
                )}
              </div>

              {/* RIGHT: Freins */}
              <MeasurementCell value={axle.right_brake} onChange={v => onUpdateAxle(idx, 'right_brake', v)} type="brake" disabled={disabled} dark={dark} />
              {/* RIGHT: Pneu Int */}
              <MeasurementCell value={axle.right_tire_inner} onChange={v => onUpdateAxle(idx, 'right_tire_inner', v)} type="tire" disabled={disabled} dark={dark} />
              {/* RIGHT: Pneu Ext */}
              <MeasurementCell value={axle.right_tire_outer} onChange={v => onUpdateAxle(idx, 'right_tire_outer', v)} type="tire" disabled={disabled || !axle.has_dual_tires} dark={dark} />
            </div>
          ))}

          {/* ARRIERE label */}
          <div className="grid grid-cols-[1fr_1fr_1fr_80px_1fr_1fr_1fr] gap-1 mt-1">
            <div className="col-span-3" />
            <div className={`text-center text-[10px] font-semibold ${textMuted}`}>ARRIERE</div>
            <div className="col-span-3" />
          </div>
        </div>

        {/* Legend */}
        <div className={`flex items-center gap-4 flex-wrap mt-4 pt-3 border-t ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
          <span className={`text-xs ${textSecondary} font-medium`}>Legende:</span>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /><span className={`text-xs ${textSecondary}`}>Bon</span></div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500" /><span className={`text-xs ${textSecondary}`}>Attention</span></div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500" /><span className={`text-xs ${textSecondary}`}>Critique</span></div>
          <span className={`text-xs ${textMuted} ml-auto`}>Freins: {unitLabel(brakeUnit || 'mm')} / Pneus: {unitLabel(tireUnit || 'mm')}</span>
        </div>
      </div>
    </div>
  );
}
